import { useCallback, useEffect, useState } from "react";
import { useAIPageContext } from "../context/AIAssistantContext";
import { useTranslation } from "react-i18next";
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Divider, MenuItem,
  Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import { getApiError } from "../api/errors";
import { getProductVariantConfiguration, searchProducts } from "../api/products";
import {
  createPurchaseInvoice, getPurchaseInvoice, getPurchaseInvoices, postPurchaseInvoice,
  recordPurchaseInvoicePayment, replacePurchaseInvoiceDraft,
} from "../api/purchaseInvoices";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";

const today = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const blankLine = () => ({ product_public_id: "", product_name: "", has_variants: false, product_variant_public_id: "", quantity: "1", unit_cost: "" });
const blankForm = () => ({ supplier_name: "", supplier_invoice_number: "", invoice_date: today(), notes: "", lines: [blankLine()] });
const formatAmount = (value) => Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (value) => {
  const [year, month, day] = String(value).split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
};
const newPaymentForm = () => ({ amount: "", payment_date: today(), method: "cash", reference: "", notes: "", idempotency_key: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}` });

function PostedInvoiceDocument({ invoice }) {
  const { t } = useTranslation();
  return (
    <Box component="section" aria-label={t("purchase.documentAria")}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1} mb={3}>
        <Typography variant="h5" fontWeight={900}>{t("purchase.document")}</Typography>
        <Chip label={t("purchase.posted")} color="success" sx={{ alignSelf: "flex-start", fontWeight: 800 }} />
      </Stack>
      <Box component="section" aria-label={t("purchase.details")} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 2, mb: 2 }}>
        {[
          [t("purchase.supplier"), invoice.supplier_name],
          [t("purchase.reference"), invoice.supplier_invoice_number || "—"],
          [t("purchase.date"), formatDate(invoice.invoice_date)],
        ].map(([label, value]) => (
          <Box key={label} sx={{ minWidth: 0, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>{value}</Typography>
          </Box>
        ))}
      </Box>
      {invoice.notes && <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">{t("common.notes")}</Typography><Typography sx={{ whiteSpace: "pre-wrap" }}>{invoice.notes}</Typography></Box>}
      <Divider sx={{ mb: 2 }} />
      <TableContainer sx={{ display: { xs: "none", sm: "block" } }}>
        <Table size="small" aria-label={t("purchase.lineItems")}>
          <TableHead><TableRow>
            <TableCell>{t("common.product")}</TableCell><TableCell>{t("purchase.exactVariant")}</TableCell>
            <TableCell align="right">{t("common.quantity")}</TableCell><TableCell align="right">{t("purchase.unitCost")}</TableCell>
            <TableCell align="right">{t("purchase.lineTotal")}</TableCell>
          </TableRow></TableHead>
          <TableBody>{invoice.lines.map((line) => (
            <TableRow key={line.public_id}>
              <TableCell>{line.product_name}</TableCell>
              <TableCell>{line.variant_label || "—"}</TableCell>
              <TableCell align="right">{line.quantity}</TableCell>
              <TableCell align="right">{formatAmount(line.unit_cost)}</TableCell>
              <TableCell align="right">{formatAmount(line.line_total)}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </TableContainer>
      <Stack spacing={1} sx={{ display: { xs: "flex", sm: "none" } }}>
        {invoice.lines.map((line) => (
          <Box key={line.public_id} sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 1.5 }}>
            <Typography fontWeight={800}>{line.product_name}</Typography>
            {line.variant_label && <Typography color="text.secondary">{line.variant_label}</Typography>}
            <Typography variant="body2">{t("purchase.quantityLabel", { quantity: line.quantity, cost: formatAmount(line.unit_cost) })}</Typography>
            <Typography variant="body2" fontWeight={700}>{t("purchase.lineTotalLabel", { value: formatAmount(line.line_total) })}</Typography>
          </Box>
        ))}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Box component="section" aria-label={t("purchase.paymentSummary")} sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.5 }}>
        {[
          [t("purchase.invoiceTotal"), formatAmount(invoice.total_amount)],
          [t("purchase.paid"), formatAmount(invoice.paid_amount || 0)],
          [t("purchase.remaining"), formatAmount(invoice.remaining_amount ?? invoice.total_amount)],
        ].map(([label, value]) => (
          <Box key={label} sx={{ minWidth: 0, p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography fontWeight={900} sx={{ overflowWrap: "anywhere" }}>{value}</Typography>
          </Box>
        ))}
        <Box sx={{ minWidth: 0, p: 1.5, bgcolor: "action.hover", borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{t("common.status")}</Typography>
          <Chip label={t(invoice.payment_status === "PAID" ? "purchase.paidStatus" : invoice.payment_status === "PARTIALLY_PAID" ? "purchase.partiallyPaid" : "purchase.unpaid")} color={invoice.payment_status === "PAID" ? "success" : "default"} size="small" />
        </Box>
      </Box>
    </Box>
  );
}

function LineEditor({ line, onChange, onRemove, disabled }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!search.trim()) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      searchProducts(search.trim(), 30)
        .then((response) => { if (live) setOptions((response.data?.items || []).filter((item) => item.track_inventory !== false)); })
        .catch(() => { if (live) setOptions([]); });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [search]);

  useEffect(() => {
    if (!line.product_public_id || !line.has_variants) {
      setVariants([]);
      return undefined;
    }
    let live = true;
    getProductVariantConfiguration(line.product_public_id)
      .then((response) => { if (live) setVariants((response.data?.variants || []).filter((item) => item.is_active)); })
      .catch(() => { if (live) setVariants([]); });
    return () => { live = false; };
  }, [line.product_public_id, line.has_variants]);

  const selected = line.product_public_id ? {
    public_id: line.product_public_id,
    name: line.product_name,
  } : null;

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
      <Autocomplete
        sx={{ minWidth: 240, flex: 2 }}
        options={options}
        filterOptions={(items) => items}
        value={selected}
        getOptionLabel={(item) => item.name || ""}
        isOptionEqualToValue={(a, b) => a.public_id === b.public_id}
        onInputChange={(_, value) => setSearch(value)}
        onChange={(_, item) => onChange({
          ...line,
          product_public_id: item?.public_id || "",
          product_name: item?.name || "",
          has_variants: Boolean(item?.has_variants),
          product_variant_public_id: "",
        })}
        disabled={disabled}
        renderInput={(params) => <TextField {...params} label={t("purchase.trackedProduct")} required />}
      />
      {line.has_variants && (
        <TextField label={t("purchase.exactVariant")} select required sx={{ minWidth: 180, flex: 1 }}
          value={line.product_variant_public_id || ""} disabled={disabled}
          onChange={(event) => onChange({ ...line, product_variant_public_id: event.target.value })}>
          {variants.map((variant) => <MenuItem key={variant.public_id} value={variant.public_id}>{variant.label}</MenuItem>)}
        </TextField>
      )}
      <TextField label={t("common.quantity")} type="number" required slotProps={{ htmlInput: { min: 1, step: 1 } }} sx={{ width: 120 }}
        value={line.quantity} disabled={disabled}
        onChange={(event) => onChange({ ...line, quantity: event.target.value })} />
      <TextField label={t("purchase.unitCost")} type="number" required slotProps={{ htmlInput: { min: 0, step: "0.01" } }} sx={{ width: 170 }}
        value={line.unit_cost} disabled={disabled}
        onChange={(event) => onChange({ ...line, unit_cost: event.target.value })} />
      <Button color="error" onClick={onRemove} disabled={disabled}>{t("purchase.remove")}</Button>
    </Stack>
  );
}

export default function PurchaseInvoices() {
  const { t } = useTranslation();
  const { selectedStore, selectedWorkspace } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(blankForm);
  const [draftId, setDraftId] = useState("");
  const [postedInvoice, setPostedInvoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(newPaymentForm);
  const invoiceId = postedInvoice?.public_id || draftId;
  const focusAIAction = useAIPageContext({ version: 1, page_id: invoiceId ? "purchase_invoices.detail" : "purchase_invoices", section: "items", ...(invoiceId ? { entity_type: "purchase_invoice", entity_public_id: invoiceId } : {}) });

  const load = useCallback(async () => {
    try {
      const response = await getPurchaseInvoices({ page, page_size: 20 });
      setItems(response.data?.items || []);
      setTotal(Number(response.data?.total || 0));
    } catch (requestError) {
      setError(getApiError(requestError, t("purchase.loadError")));
    }
  }, [page, t]);

  useEffect(() => { load(); }, [load, selectedStore?.public_id, selectedWorkspace?.public_id]);

  const choose = (invoice) => {
    setPostedInvoice(invoice.status === "POSTED" ? invoice : null);
    setDraftId(invoice.status === "DRAFT" ? invoice.public_id : "");
    setForm({
      supplier_name: invoice.supplier_name,
      supplier_invoice_number: invoice.supplier_invoice_number || "",
      invoice_date: invoice.invoice_date,
      notes: invoice.notes || "",
      lines: invoice.lines.map((line) => ({
        product_public_id: line.product_public_id,
        product_name: line.product_name,
        has_variants: Boolean(line.product_variant_public_id),
        product_variant_public_id: line.product_variant_public_id || "",
        quantity: String(line.quantity),
        unit_cost: String(line.unit_cost),
      })),
    });
    setNotice(t(invoice.status === "POSTED" ? "purchase.immutable" : "purchase.draftLoaded"));
    setError("");
    setPaymentOpen(false);
    setPaymentForm(newPaymentForm());
  };

  const pay = async (event) => {
    event.preventDefault();
    if (!postedInvoice || !paymentForm.amount || Number(paymentForm.amount) <= 0) return;
    setBusy(true);
    setError("");
    try {
      await recordPurchaseInvoicePayment(postedInvoice.public_id, paymentForm);
      const updated = await getPurchaseInvoice(postedInvoice.public_id);
      setPostedInvoice(updated.data);
      setPaymentForm(newPaymentForm());
      setPaymentOpen(false);
      setNotice(t("purchase.paymentRecorded"));
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("purchase.paymentError")));
    } finally {
      setBusy(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (postedInvoice) return;
    setError("");
    setNotice("");
    const lines = form.lines.map((line) => ({
      product_public_id: line.product_public_id,
      product_variant_public_id: line.product_variant_public_id || null,
      quantity: Number(line.quantity),
      unit_cost: line.unit_cost,
    }));
    if (!lines.length || lines.some((line) => !line.product_public_id || line.quantity < 1 || line.unit_cost === "")) {
      setError(t("purchase.lineError"));
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, lines };
      const response = draftId
        ? await replacePurchaseInvoiceDraft(draftId, payload)
        : await createPurchaseInvoice(payload);
      setDraftId(response.data.public_id);
      setNotice(t("purchase.draftSaved"));
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("purchase.draftError")));
    } finally {
      setBusy(false);
    }
  };

  const post = async () => {
    if (!draftId) return;
    setBusy(true);
    setError("");
    try {
      await postPurchaseInvoice(draftId);
      setDraftId("");
      setNotice(t("purchase.postedSuccess"));
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("purchase.postError")));
    } finally {
      setBusy(false);
    }
  };

  const updateLine = (index, next) => setForm((current) => ({
    ...current, lines: current.lines.map((line, position) => position === index ? next : line),
  }));

  return (
    <MainLayout>
      <Typography variant="h4" fontWeight={900} mb={1}>{t("purchase.title")}</Typography>
      <Typography color="text.secondary" mb={3}>{t("purchase.subtitle", { store: selectedStore?.name || t("purchase.selectedStore") })}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      <Card sx={{ mb: 3 }}><CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={800}>{t(postedInvoice ? "purchase.postedInvoice" : draftId ? "purchase.editDraft" : "purchase.newDraft")}</Typography>
          <Button onClick={() => { setDraftId(""); setPostedInvoice(null); setForm(blankForm()); setNotice(""); }}>{t("purchase.newInvoice")}</Button>
        </Stack>
        {postedInvoice ? <><PostedInvoiceDocument invoice={postedInvoice} />
          {postedInvoice.payment_status !== "PAID" && <Box mt={3}>
            <Button onClick={() => setPaymentOpen((current) => !current)} disabled={busy}>{t("purchase.recordPayment")}</Button>
            {paymentOpen && <Box component="form" onSubmit={pay} mt={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                <TextField label={t("purchase.paymentAmount")} type="number" required value={paymentForm.amount} inputProps={{ min: 0.01, step: "0.01", max: Number(postedInvoice.remaining_amount ?? postedInvoice.total_amount) }} onChange={(event) => setPaymentForm({ ...paymentForm, amount: event.target.value })} />
                <TextField label={t("purchase.paymentDate")} type="date" required value={paymentForm.payment_date} slotProps={{ inputLabel: { shrink: true } }} onChange={(event) => setPaymentForm({ ...paymentForm, payment_date: event.target.value })} />
                <TextField label={t("purchase.paymentMethod")} required value={paymentForm.method} onChange={(event) => setPaymentForm({ ...paymentForm, method: event.target.value })} />
                <TextField label={t("purchase.reference")} value={paymentForm.reference} onChange={(event) => setPaymentForm({ ...paymentForm, reference: event.target.value })} />
              </Stack>
              <TextField label={t("purchase.paymentNotes")} fullWidth sx={{ mt: 1.5 }} value={paymentForm.notes} onChange={(event) => setPaymentForm({ ...paymentForm, notes: event.target.value })} />
              <Button type="submit" variant="contained" sx={{ mt: 1.5 }} disabled={busy}>{t("purchase.savePayment")}</Button>
            </Box>}
          </Box>}
        </> : <Box component="form" onSubmit={save}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
            <TextField label={t("purchase.supplier")} required value={form.supplier_name} onChange={(event) => setForm({ ...form, supplier_name: event.target.value })} />
            <TextField label={t("purchase.supplierInvoiceNumber")} value={form.supplier_invoice_number} onChange={(event) => setForm({ ...form, supplier_invoice_number: event.target.value })} />
            <TextField label={t("purchase.invoiceDate")} type="date" required value={form.invoice_date} slotProps={{ inputLabel: { shrink: true } }} onChange={(event) => setForm({ ...form, invoice_date: event.target.value })} />
          </Stack>
          <TextField label={t("common.notes")} fullWidth multiline minRows={2} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} sx={{ mb: 2 }} />
          <Stack spacing={2} mb={2}>
            {form.lines.map((line, index) => (
              <LineEditor key={index} line={line} onChange={(next) => updateLine(index, next)}
                onRemove={() => setForm((current) => ({ ...current, lines: current.lines.filter((_, position) => position !== index) }))}
                disabled={busy} />
            ))}
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setForm({ ...form, lines: [...form.lines, blankLine()] })} disabled={busy}>{t("purchase.addLine")}</Button>
            <Button type="submit" variant="contained" disabled={busy}>{t("purchase.saveDraft")}</Button>
            {draftId && <Button color="success" variant="contained" onFocus={() => focusAIAction("purchase_invoices.post")} onMouseEnter={() => focusAIAction("purchase_invoices.post")} onClick={post} disabled={busy}>{t("purchase.postReceive")}</Button>}
          </Stack>
        </Box>}
      </CardContent></Card>
      <Typography variant="h6" fontWeight={800} mb={1}>{t("purchase.history")}</Typography>
      <Stack spacing={1}>
        {items.map((invoice) => (
          <Card key={invoice.public_id}><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
              <Box>
                <Typography fontWeight={800}>{invoice.supplier_name} · {invoice.supplier_invoice_number || invoice.public_id}</Typography>
              <Typography variant="body2" color="text.secondary">{invoice.invoice_date} · {t(`purchase.${invoice.status.toLowerCase()}`, { defaultValue: invoice.status })} · {t("purchase.linesCount", { count: invoice.lines.length })} · {invoice.total_amount}{invoice.status === "POSTED" ? ` · ${t(invoice.payment_status === "PAID" ? "purchase.paidStatus" : invoice.payment_status === "PARTIALLY_PAID" ? "purchase.partiallyPaid" : "purchase.unpaid")}` : ""}</Typography>
              </Box>
              <Button onClick={() => choose(invoice)}>{t(invoice.status === "DRAFT" ? "common.edit" : "common.view")}</Button>
            </Stack>
            {invoice.status === "POSTED" && <Divider sx={{ mt: 1 }} />}
          </CardContent></Card>
        ))}
      </Stack>
      {total > 20 && <Pagination sx={{ mt: 2 }} page={page} count={Math.ceil(total / 20)} onChange={(_, value) => setPage(value)} />}
    </MainLayout>
  );
}
